import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Skeleton } from '@mui/material';
import { useSelector } from 'react-redux';
import ButtonLoading from '@/components/common/ButtonLoading';
import {
    useCheckAuthState,
    useToastNotification,
    useDocumentFormData,
    useSubscription
} from '@/hooks';
import {
    useSaveEstimate,
    useValidateEstimate
} from '@/hooks/useSidebarActions';
import { SaveIcon } from '@/resources/icons';
import { documentSaved as posthogDocumentSaved } from '@/modules/posthog/posthogManager';

import useStyles from '../../styles';

const EstimateSave = ({ onNegativeEstimate, onLoginBlocker }) => {
    const classes = useStyles();

    const [loading, setLoading] = useState(false);
    const { loadingUser, registeredUser } = useCheckAuthState();
    const saveEstimate = useSaveEstimate();
    const { locked, documentLoading, total } = useDocumentFormData();
    const validateEstimate = useValidateEstimate();
    const { triggerToast } = useToastNotification();
    const subscriptionData = useSubscription();

    // Used for validation.
    const estimate = useSelector((state) => state?.makeInvoice);

    const subInfo = subscriptionData?.components?.document;
    const blockDocumentCreation =
        !subInfo?.enabled || (subInfo?.limited && subInfo?.amountRemaining < 1);

    const loadingButton = loadingUser || documentLoading;

    const handleOnClick = async () => {
        setLoading(true);
        const estimateData = await validateEstimate(estimate);

        if (!estimateData) {
            setLoading(false);
            return;
        }

        if (total < 0) {
            onNegativeEstimate();
            setLoading(false);
            return;
        }

        const completeSaveProcess = async () => {
            await saveEstimate(estimateData, 'save');

            if (!blockDocumentCreation) {
                triggerToast({
                    message: 'Estimate Saved',
                    action: 'saved',
                    icon: <SaveIcon />
                });
            }

            posthogDocumentSaved(estimateData);
            setLoading(false);
        };

        // Check is a user is registered, if they are complete the process.
        // If not, see if we can get them to register.
        if (registeredUser) {
            completeSaveProcess();
        } else {
            onLoginBlocker(completeSaveProcess);
        }
    };

    if (!locked) {
        return (
            <div className={classes.section}>
                {!loadingButton ? (
                    <ButtonLoading
                        fullWidth
                        className={classes.button}
                        variant="contained"
                        color="secondary"
                        size="large"
                        endIcon={<SaveIcon className={classes.buttonSaveSvg} />}
                        onClick={handleOnClick}
                        disableOnLoading
                        loading={loading}
                    >
                        Save
                    </ButtonLoading>
                ) : (
                    <Skeleton variant="rectangular" className={classes.sizer} />
                )}
            </div>
        );
    }
    return null;
};

EstimateSave.propTypes = {
    onNegativeEstimate: PropTypes.func.isRequired,
    onLoginBlocker: PropTypes.func.isRequired
};

export default EstimateSave;
